import * as React from 'react';
import { Dropdown } from 'semantic-ui-react';

import { Values, ValuesObject } from './Values';

export type ActionProps<T extends ValuesObject> = {
    key: string | number;
    text: React.ReactNode;
    disabled?: boolean;
    onClick: (values: Values<T>, event: React.MouseEvent<HTMLDivElement>) => T | Promise<T>;
};

export type ItemProps<TId extends string = string> = {
    value: TId;
    text: React.ReactNode;
    disabled?: boolean;
};

export type ItemsPropsFor<T> = ItemProps<Extract<keyof T, string>>;

export type CheckboxMenuProps<T extends ValuesObject> = Readonly<{
    /**
     * Objeto donde cada clave es un item y su valor determina si este está
     * seleccionado o no.
     */
    values: T;
    /**
     * Función que será llamada con todos los valores actualizados cada vez
     * que uno de los items cambie su valor.
     */
    onChange: (values: T) => void;
    /**
     * Arreglo con cada elemento que se mostrará como checkbox en el menú.
     * La propiedad `value` debe ser la clave de alguna de las propiedades
     * del objeto `values` mientras que `text` es el texto que se mostrará
     * como item en el menú. Cada `value` debe ser único.
     */
    items: ReadonlyArray<ItemsPropsFor<T>>;
    /**
     * Función que será llamada con un objeto indicando la cantidad de items
     * seleccionados y deseleccionados, y cuyo valor de retorno se usará como
     * título del Dropdown.
     */
    title: React.ReactNode | ((props: CheckboxMenuProps<T>) => React.ReactNode);
    actions?: ReadonlyArray<ActionProps<T>>;
}>;

type State = Readonly<{
    isOpen: boolean;
}>;

export class CheckboxMenu<T extends { [value: string]: boolean }> extends React.Component<
    CheckboxMenuProps<T>,
    State
> {
    public readonly state: State = {
        isOpen: false,
    };

    public render() {
        const { values, items, actions } = this.props;

        return (
            <Dropdown
                trigger={this.title()}
                open={this.state.isOpen}
                onClick={this.openMenu}
                onFocus={this.openMenu}
                onBlur={this.closeMenu}
                onKeyDown={(ev: React.KeyboardEvent) => {
                    if (ev.key === 'Escape') this.closeMenu();
                }}
            >
                <Dropdown.Menu>
                    <Dropdown.Item content='Seleccionar todos' onClick={this.handleSelectAll} />
                    <Dropdown.Item content='Seleccionar ninguno' onClick={this.handleSelectNone} />

                    {actions &&
                        actions.map(action => (
                            <Dropdown.Item
                                key={action.key}
                                content={action.text}
                                disabled={action.disabled}
                                onClick={ev => {
                                    // tslint:disable-next-line: no-floating-promises
                                    Promise.resolve(action.onClick(Values(values), ev)).then(
                                        this.props.onChange,
                                    );
                                }}
                            />
                        ))}

                    <Dropdown.Menu scrolling>
                        {items.map(item => (
                            <CheckboxMenuItem
                                key={item.value}
                                {...item}
                                checked={values[item.value]}
                                onClick={ev => this.handleItemClick(item.value, ev)}
                            />
                        ))}
                    </Dropdown.Menu>

                    <Dropdown.Header style={{ textTransform: 'none' }}>
                        <code style={{ paddingRight: '3pt' }}>Alt + clic</code> selecciona solo uno
                    </Dropdown.Header>
                </Dropdown.Menu>
            </Dropdown>
        );
    }

    private readonly handleItemClick = (
        value: keyof T,
        event: React.MouseEvent<HTMLDivElement>,
    ) => {
        const { values, onChange } = this.props;

        if (event.altKey) {
            onChange(Values(values).withAllUncheckedExcept(value));
        } else {
            onChange(Values(values).withValueToggled(value));
        }
    };

    private readonly handleSelectAll = () => {
        const { values, onChange } = this.props;

        onChange(Values(values).withAllChecked());
    };

    private readonly handleSelectNone = () => {
        const { values, onChange } = this.props;

        onChange(Values(values).withAllUnchecked());
    };

    private readonly openMenu = () => {
        this.setState({ isOpen: true });
    };

    private readonly closeMenu = () => {
        this.setState({ isOpen: false });
    };

    private title() {
        const { title } = this.props;

        return typeof title === 'function' ? title(this.props) : title;
    }
}

const CheckboxMenuItem = (props: {
    checked: boolean;
    text: React.ReactNode;
    disabled?: boolean;
    onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}) => {
    const { checked, text, onClick, ...resto } = props;

    return (
        <Dropdown.Item
            {...resto}
            content={text}
            onClick={onClick}
            icon={{
                name: 'check',
                style: !checked ? { opacity: 0 } : undefined,
            }}
        />
    );
};



// WEBPACK FOOTER //
// ./src/componentes/CheckboxMenu/CheckboxMenu.tsx